export const INIT_OVERVIEW_SCHOOL_YEAR_KEY = "initOverviewSchoolYearKey";

const keysEqual = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * Normalize school year display names like "2025-26" → "2025-2026".
 * Leaves already-full or unexpected formats unchanged.
 * @param {string} [name]
 * @returns {string|undefined}
 */
export const formatSchoolYearName = (name) => {
    const match = /^(\d{4})-(\d{2})$/.exec(name || "");
    if (!match) return name;

    const startYear = Number(match[1]);
    const shortEndYear = Number(match[2]);
    const startCentury = Math.floor(startYear / 100) * 100;
    const endYear = startCentury + shortEndYear + (shortEndYear < startYear % 100 ? 100 : 0);

    return `${startYear}-${endYear}`;
};

export const labelSchoolYearOption = (year) => (year?.active ? "Current Year" : year?.name);

export const resolveInitialSchoolYearKey = ({ years = [], handoffKey, activeKey }) => {
    if (handoffKey != null && handoffKey !== "") {
        const match = years.find((y) => keysEqual(y.key, handoffKey));
        if (match) return match.key;
    }
    if (activeKey != null) {
        const active = years.find((y) => keysEqual(y.key, activeKey));
        if (active) return active.key;
        return activeKey;
    }
    return years[0]?.key;
};

export const shouldDisableSecondSemesterTab = ({ selectedKey, activeKey, currentPeriodName }) =>
    keysEqual(selectedKey, activeKey) && currentPeriodName === "1st Semester";

export const buildOverviewSyParam = ({ selectedKey, activeKey }) => {
    if (selectedKey == null || keysEqual(selectedKey, activeKey)) return undefined;
    return selectedKey;
};

/**
 * Keep years that have started (startDate <= now). Always keep active years.
 * @param {Array} years
 * @param {Date} [now=new Date()]
 * @returns {Array}
 */
export const filterStartedSchoolYears = (years = [], now = new Date()) => {
    const nowMs = now.getTime();
    return years.filter((year) => {
        if (year?.active) return true;
        if (!year?.startDate) return false;
        const startMs = new Date(year.startDate).getTime();
        if (Number.isNaN(startMs)) return false;
        return startMs <= nowMs;
    });
};

const yearStartMs = (year) => {
    if (!year?.startDate) return Number.NaN;
    return new Date(year.startDate).getTime();
};

/**
 * Keep active year + single most recent prior year.
 * Drops years with startDate after the active year's startDate.
 * @param {Array} years
 * @returns {Array}
 */
export const filterCurrentAndPreviousSchoolYears = (years = []) => {
    const active = years.find((y) => y?.active);
    if (!active) return [];

    const activeStart = yearStartMs(active);
    const previousCandidates = years.filter((y) => {
        if (y === active || y?.active) return false;
        const ms = yearStartMs(y);
        if (Number.isNaN(ms)) return false;
        return !(!Number.isNaN(activeStart) && ms > activeStart);
    });

    if (previousCandidates.length === 0) return [active];

    previousCandidates.sort((a, b) => {
        const diff = yearStartMs(b) - yearStartMs(a);
        if (diff !== 0) return diff;
        return Number(b.key) - Number(a.key);
    });

    return [active, previousCandidates[0]];
};

export const readAndClearOverviewSchoolYearHandoff = () => {
    const value = sessionStorage.getItem(INIT_OVERVIEW_SCHOOL_YEAR_KEY);
    sessionStorage.removeItem(INIT_OVERVIEW_SCHOOL_YEAR_KEY);
    return value;
};

export const writeOverviewSchoolYearHandoff = (selectedKey) => {
    if (selectedKey == null) return;
    sessionStorage.setItem(INIT_OVERVIEW_SCHOOL_YEAR_KEY, String(selectedKey));
};
